// Imports for external libraries go here.
import React, { FC, useRef, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { toggleSelectedConsentCheckbox } from '../../../utils/consents';
import { StyledConsentsList } from './ConsentsList.styles';
import { ConsentsCheckListModalInfo, ConsentsListProps } from './ConsentsList.types';
import { ConsentsListItem } from './ConsentsListItem';
import { ConsentTermModal } from '../../../molecules/TermModalBody';

const DEFAULT_MODAL_STATE = {
  showModal: false,
  href: '',
};
// Use named rather than default exports.
export const ConsentsList: FC<ConsentsListProps> = ({
  consentState,
  onConsentChange,
  validateErrors,
  anchorBehavior,
  linkBehavior,
  closeAccordionBtnLabel,
  openAccordionBtnLabel,
}) => {
  const [consentModalInfo, setConsentModalInfo] = useState<ConsentsCheckListModalInfo>(DEFAULT_MODAL_STATE);
  const { showModal } = consentModalInfo;
  const activeAnchorElementBeforeModalOpen = useRef<HTMLElement | null>(null); // Track the element that opened the modal

  //Handling popup modal
  const onModalOpen = async (e: React.MouseEvent, hrefLink: string) => {
    e.preventDefault();
    activeAnchorElementBeforeModalOpen.current = e.target as HTMLElement;
    setConsentModalInfo({
      href: hrefLink,
      showModal: true,
    });
  };

  const onModalClose = () => {
    setConsentModalInfo(DEFAULT_MODAL_STATE);
    if (activeAnchorElementBeforeModalOpen.current) {
      activeAnchorElementBeforeModalOpen.current.focus();
    }
  };

  const onCheckboxToggle = (_selectedConsentId: string, selectedCheckboxName: string, sectionType: string) => {
    const updatedConsentsState = toggleSelectedConsentCheckbox({
      consentState,
      selectedCheckboxName,
      sectionType,
    });
    console.log('updatedConsentsState ', updatedConsentsState);
    onConsentChange({ updatedConsentsState });
  };

  // Usage for header and checkbox
  const onLinkClickInHeader = (e: React.MouseEvent<HTMLDivElement>) => {
    onLinkClick(e, '.js-header-list');
  };

  const onLinkClickInCheckbox = (e: React.MouseEvent<HTMLDivElement>) => {
    onLinkClick(e, '.js-checkbox-label');
  };

  const onLinkClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, containerClass: string) => {
    const target = e.target as HTMLElement;

    const isModalLink = target.getAttribute('data-is-modal') === 'true';

    // Check if the clicked target is an anchor tag inside the specified container
    if (target.closest(containerClass) && target.tagName === 'A') {
      const href = target.getAttribute('href') ?? '';
      if (isModalLink) {
        e.preventDefault();
        onModalOpen(e, href); // Open the modal with the href of the link
      }
      // reset browser will handle itself i.e. _blank , _self
    }
  };

  return (
    <StyledConsentsList
      className={`anchor__${anchorBehavior}`}
      data-testid="legal-ConsentList"
      data-component-name="o-legal-ConsentList"
    >
      {consentState.list.map(consentData => (
        <ConsentsListItem
          key={`consentListItem-${consentData.id}`}
          consentData={consentData}
          showTopDivider={consentData.showTopDivider}
          showBottomDivider={consentData.showBottomDivider}
          consentSectionType={consentData.sectionType}
          validateErrors={validateErrors}
          onModalOpen={onModalOpen}
          onCheckboxToggle={onCheckboxToggle}
          onLinkClickInHeader={onLinkClickInHeader}
          onLinkClickInCheckbox={onLinkClickInCheckbox}
          linkBehavior={linkBehavior}
          closeAccordionBtnLabel={closeAccordionBtnLabel}
          openAccordionBtnLabel={openAccordionBtnLabel}
        />
      ))}
      {showModal && <ConsentTermModal href={consentModalInfo.href} onModalClose={onModalClose} />}
    </StyledConsentsList>
  );
};
